import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  User, LogOut, Settings, Shield, ChevronDown, 
  Building2, Clock, Bell, X
} from 'lucide-react';
import NotificationPreferences from './NotificationPreferences';

const UserProfileMenu = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [showNotificationPrefs, setShowNotificationPrefs] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isAuthenticated || !user) {
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const getRoleColor = (roleKey) => {
    const colors = {
      'sysadmin': 'bg-[var(--clr-sev-blocked-bg)] text-[var(--clr-sev-blocked)]',
      'pmo_power': 'bg-[var(--clr-brand-primary)] bg-opacity-10 text-[var(--clr-brand-primary)]',
      'program_admin': 'bg-[var(--clr-sev-inprogress-bg)] text-[var(--clr-sev-inprogress)]',
      'pm': 'bg-[var(--clr-sev-healthy-bg)] text-[var(--clr-sev-healthy)]',
      'viewer': 'bg-[var(--clr-surface-subtle)] text-[var(--clr-text-secondary)]'
    };
    return colors[roleKey] || 'bg-[var(--clr-surface-subtle)] text-[var(--clr-text-secondary)]';
  };

  const getRoleName = (roleKey) => {
    const names = {
      'sysadmin': 'System Admin',
      'pmo_power': 'Super User',
      'program_admin': 'Program Admin',
      'pm': 'Project Manager',
      'viewer': 'Viewer'
    };
    return names[roleKey] || roleKey;
  };

  const initials = user.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : user.email[0].toUpperCase();

  return (
    <div className="relative" ref={menuRef}>
      {/* Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-[var(--clr-surface-hover)] transition-colors"
        data-testid="user-menu-btn"
      >
        <div className="w-9 h-9 rounded-full bg-[var(--clr-brand-primary)] flex items-center justify-center text-white font-medium text-sm">
          {initials}
        </div>
        <div className="hidden md:block text-left">
          <p className="text-sm font-medium text-[var(--clr-text-primary)] leading-tight">{user.name || 'User'}</p>
          <p className="text-xs text-[var(--clr-text-muted)]">{user.roles?.[0]?.role_name || 'Member'}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-[var(--clr-text-muted)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-[var(--clr-surface-card)] rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
          {/* User Header */}
          <div className="p-4 bg-[var(--clr-surface-subtle)] border-b">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[var(--clr-brand-primary)] flex items-center justify-center text-white font-semibold text-lg">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[var(--clr-text-primary)] truncate">{user.name || 'User'}</p>
                <p className="text-sm text-[var(--clr-text-muted)] truncate">{user.email}</p>
              </div>
            </div>
            
            {/* Roles */}
            <div className="mt-3 flex flex-wrap gap-1.5">
              {user.roles?.map((role, idx) => (
                <span 
                  key={idx}
                  className={`px-2 py-0.5 text-xs font-medium rounded-full ${getRoleColor(role.role_key)}`}
                >
                  {role.role_name || getRoleName(role.role_key)}
                </span>
              ))}
            </div>
            
            {/* Tenant */}
            {user.tenant && (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-[var(--clr-text-muted)]">
                <Building2 className="w-3.5 h-3.5" />
                <span>{user.tenant.name}</span>
              </div>
            )}
          </div>

          {/* Menu Items */}
          <div className="py-2">
            <button
              onClick={() => { setIsOpen(false); navigate('/profile'); }}
              className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-[var(--clr-surface-hover)] text-left"
              data-testid="menu-profile"
            >
              <User className="w-4 h-4 text-[var(--clr-text-muted)]" />
              <span className="text-sm text-gray-700">My Profile</span>
            </button>
            
            <button
              onClick={() => setShowPermissions(true)}
              className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-[var(--clr-surface-hover)] text-left"
              data-testid="menu-permissions"
            >
              <Shield className="w-4 h-4 text-[var(--clr-text-muted)]" />
              <div className="flex-1">
                <span className="text-sm text-gray-700">My Permissions</span>
                {user.overrides?.length > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 text-xs bg-[var(--clr-sev-atrisk-bg)] text-[var(--clr-sev-atrisk)] rounded">
                    {user.overrides.length} override{user.overrides.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </button>
            
            <button
              onClick={() => { setIsOpen(false); setShowNotificationPrefs(true); }}
              className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-[var(--clr-surface-hover)] text-left"
              data-testid="menu-notifications"
            >
              <Bell className="w-4 h-4 text-[var(--clr-text-muted)]" />
              <span className="text-sm text-gray-700">Notification Preferences</span>
            </button>
            
            <button
              onClick={() => { setIsOpen(false); navigate('/settings'); }}
              className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-[var(--clr-surface-hover)] text-left"
              data-testid="menu-settings"
            >
              <Settings className="w-4 h-4 text-[var(--clr-text-muted)]" />
              <span className="text-sm text-gray-700">Settings</span>
            </button>
          </div>

          {/* Logout */}
          <div className="border-t py-2">
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-[var(--clr-sev-blocked)] bg-opacity-10 text-left"
              data-testid="menu-logout"
            >
              <LogOut className="w-4 h-4 text-[var(--clr-sev-blocked)]" />
              <span className="text-sm text-[var(--clr-sev-blocked)] font-medium">Sign Out</span>
            </button>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowPermissions(false)} />
          <div className="relative bg-[var(--clr-surface-card)] rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">My Permissions</h2>
              <button 
                onClick={() => setShowPermissions(false)}
                className="p-1 hover:bg-[var(--clr-surface-hover)] rounded"
              >
                <X className="w-5 h-5 text-[var(--clr-text-muted)]" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {/* Role-based Permissions */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Role Permissions
                </h3>
                <div className="space-y-2">
                  {user.roles?.map((role, idx) => (
                    <div key={idx} className="p-3 bg-[var(--clr-surface-subtle)] rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getRoleColor(role.role_key)}`}>
                          {role.role_name || getRoleName(role.role_key)}
                        </span>
                        <span className="text-xs text-[var(--clr-text-muted)]">
                          {role.scope_type === 'global' ? 'Global' : `${role.scope_type}: ${role.scope_id?.slice(-6)}`}
                        </span>
                      </div>
                      <p className="text-xs text-[var(--clr-text-secondary)]">
                        {role.permission_count || 0} permissions granted by this role
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Active Overrides */}
              {user.overrides && user.overrides.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Active Overrides
                  </h3>
                  <div className="space-y-2">
                    {user.overrides.map((override, idx) => (
                      <div key={idx} className="p-3 bg-[var(--clr-sev-atrisk)] bg-opacity-10 border border-[var(--clr-sev-atrisk-border)] rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-[var(--clr-text-primary)]">
                            {override.permission_key.replace(/_/g, ' ')}
                          </span>
                          <span className="px-2 py-0.5 text-xs bg-[var(--clr-sev-atrisk)] text-white rounded-full">
                            Temporary
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-[var(--clr-sev-atrisk)]">
                          <span>Scope: {override.scope_type}</span>
                          {override.expires_at && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Expires: {new Date(override.expires_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        {override.reason && (
                          <p className="mt-2 text-xs text-[var(--clr-sev-atrisk)] italic">"{override.reason}"</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {(!user.overrides || user.overrides.length === 0) && (
                <div className="text-center py-4 text-[var(--clr-text-muted)] text-sm">
                  No active permission overrides
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notification Preferences Panel */}
      <NotificationPreferences 
        isOpen={showNotificationPrefs} 
        onClose={() => setShowNotificationPrefs(false)} 
      />
    </div>
  );
};

export default UserProfileMenu;
